import React, { useMemo, useState } from "react";

import { Play } from "lucide-react";

import { Video } from "../../Store/Reducers/VideoSlice";
import { groupVideosByTopic } from "../../Utils/Helper/videoTopics";
import CarouselNavigation from "../CarouselNavigation";
import TabsComponent from "../Tab";
import TruncatedTooltip from "../TruncatedTooltip";

export interface HomeVideosSectionProps {
  videos: Video[];
  loading?: boolean;
  /** Optional className for the section wrapper (e.g. executive-sponsore-home__section or c-level-home__section) */
  sectionClassName?: string;
  /** Optional className for video container (e.g. executive-sponsore-home__video-wrap) */
  videoWrapClassName?: string;
  /** Optional className for empty placeholder */
  placeholderClassName?: string;
  /** Whether to show description below the title */
  showDescription?: boolean;
}

function extractVideoId(url: string): string | null {
  try {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/,
      /youtube\.com\/embed\/([^&\s]+)/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) return match[1];
    }
    return null;
  } catch {
    return null;
  }
}

function isYouTubeUrl(url: string): boolean {
  return /youtube\.com|youtu\.be/.test(url);
}

const HomeVideosSection: React.FC<HomeVideosSectionProps> = ({
  videos: allVideos,
  loading = false,
  sectionClassName = "",
  videoWrapClassName = "",
  placeholderClassName = "",
  showDescription = true,
}) => {
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [activeTopicKey, setActiveTopicKey] = useState<string | null>(null);

  const topicGroups = useMemo(
    () =>
      allVideos.some(video => video.topic_id)
        ? groupVideosByTopic(allVideos, "Other")
        : [],
    [allVideos]
  );
  const activeGroup =
    topicGroups.find(group => group.key === activeTopicKey) ?? topicGroups[0];
  const videos = activeGroup ? activeGroup.videos : allVideos;

  const handleTopicChange = (key: string) => {
    setActiveTopicKey(key);
    setActiveVideoIndex(0);
  };

  const currentVideo = useMemo(
    () => videos[activeVideoIndex] || null,
    [videos, activeVideoIndex]
  );
  const currentVideoId = currentVideo
    ? extractVideoId(currentVideo.video_url)
    : null;
  const isCurrentVideoYouTube = currentVideo
    ? isYouTubeUrl(currentVideo.video_url)
    : false;

  const handlePrevVideo = () => {
    setActiveVideoIndex(prev => Math.max(0, prev - 1));
  };

  const handleNextVideo = () => {
    setActiveVideoIndex(prev => Math.min(videos.length - 1, prev + 1));
  };

  const sectionCls =
    sectionClassName || "bg-white rounded-xl p-6 border border-gray-200 h-fit";
  const wrapCls =
    videoWrapClassName ||
    "relative w-full h-[240px] sm:h-[280px] rounded-2xl overflow-hidden bg-gray-100 mb-3";
  const placeholderCls =
    placeholderClassName ||
    "w-full h-[240px] sm:h-[280px] rounded-2xl overflow-hidden border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-3 text-gray-500";

  return (
    <div className={sectionCls}>
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-1 tracking-tight">
          Video Tutorials
        </h2>
      </div>
      {!loading && topicGroups.length > 0 && activeGroup && (
        <div className="mb-3">
          <TabsComponent
            tabs={topicGroups.map(group => ({
              id: group.key,
              label: group.label,
              count: group.videos.length,
            }))}
            activeTab={activeGroup.key}
            onTabChange={handleTopicChange}
            variant="tab-underline"
            scrollable
          />
        </div>
      )}
      {loading ? (
        <div className={`${placeholderCls} border-solid`}>
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-purple-600" />
          <span className="text-sm font-medium">Loading videos...</span>
        </div>
      ) : videos.length > 0 ? (
        <div className="w-full">
          {currentVideo && (
            <>
              <div className="mb-4 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-1.5 rounded-full bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-700">
                    <Play
                      className="h-3.5 w-3.5 shrink-0"
                      strokeWidth={2.5}
                      aria-hidden
                    />
                    {showDescription && currentVideo.description ? (
                      <div className="min-w-0 flex-1 overflow-hidden [&_.truncated-tooltip__parent]:!w-full [&_.truncated-tooltip__parent]:!max-w-full">
                        <TruncatedTooltip tooltip={currentVideo.description}>
                          {currentVideo.description}
                        </TruncatedTooltip>
                      </div>
                    ) : (
                      <span className="truncate">
                        {currentVideo.title ||
                          "Watch videos related to your role"}
                      </span>
                    )}
                  </div>
                </div>
                {videos.length > 1 && (
                  <span className="shrink-0 text-xs font-medium text-gray-500 whitespace-nowrap">
                    {activeVideoIndex + 1} of {videos.length}
                  </span>
                )}
              </div>
              <div className={wrapCls}>
                {isCurrentVideoYouTube && currentVideoId ? (
                  <iframe
                    className="absolute inset-0 w-full h-full rounded-2xl"
                    src={`https://www.youtube.com/embed/${currentVideoId}`}
                    title={currentVideo.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    className="w-full h-full object-contain rounded-2xl"
                    controls
                    src={currentVideo.video_url}
                    title={currentVideo.title}
                  >
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
              {videos.length > 1 && (
                <CarouselNavigation
                  activeIndex={activeVideoIndex}
                  total={videos.length}
                  onPrev={handlePrevVideo}
                  onNext={handleNextVideo}
                  disablePrev={activeVideoIndex === 0}
                  disableNext={activeVideoIndex === videos.length - 1}
                />
              )}
            </>
          )}
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-5">
            Watch videos related to your role
          </p>
          <div className={placeholderCls}>
            <div className="rounded-full bg-gray-200/80 p-4">
              <Play className="w-10 h-10 text-gray-500" strokeWidth={1.5} />
            </div>
            <span className="text-sm font-semibold text-gray-600">
              No videos available
            </span>
            <span className="text-xs text-gray-400">
              Videos will appear here once configured
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default HomeVideosSection;
